/**
 * UserProjectListPage
 * [UC-042] Display user's projects list with navigation to create new projects
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components/ui';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface Project {
  projectId: number;
  name: string;
  description: string;
  status: string;
  creationDate: string;
  user_id: number;
}

export function UserProjectListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      // Default page size is 20; bump to the backend max so the user
      // sees every project they own, not just the first page.
      const response = await api.get('/projects', { params: { limit: 100 } });
      const items: Project[] = (response as any).items || [];
      items.sort((a, b) => (b.creationDate || '').localeCompare(a.creationDate || ''));
      setProjects(items);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error(t('userProjectList.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      case 'InDevelopment':
        return 'bg-blue-100 text-blue-800';
      case 'ForTesting':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'AppPreviewPreparation':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <span className="text-gray-700">{t('userProjectList.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('userProjectList.title')}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('userProjectList.subtitle')}
            </p>
          </div>
          <Button
            onClick={() => navigate('/user-project-create')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t('userProjectList.buttons.createProject')}
          </Button>
        </div>

        {/* Search Bar */}
        <div className="max-w-md">
          <Input
            type="text"
            placeholder={t('userProjectList.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      <Card>
        {filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            {projects.length === 0 ? (
              <div>
                <div className="mb-4">
                  <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('userProjectList.empty.title')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('userProjectList.empty.description')}
                </p>
                <Button
                  onClick={() => navigate('/user-project-create')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {t('userProjectList.buttons.createFirstProject')}
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-gray-600">
                  {t('userProjectList.search.noResults')}
                </p>
                <Button
                  onClick={() => setSearchTerm('')}
                  variant="outline"
                  className="mt-4"
                >
                  {t('userProjectList.buttons.clearSearch')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProjects.map((project) => (
              <div
                key={project.projectId}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/user-project-detail/${project.projectId}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {project.name}
                    </h3>
                    <p className="text-gray-600 mb-3 line-clamp-2">
                      {project.description}
                    </p>
                    <div className="flex items-center text-sm text-gray-500">
                      <span>
                        {t('userProjectList.project.created')}: {formatDate(project.creationDate)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-6 flex flex-col items-end space-y-2">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(project.status)}`}>
                      {t(`userProjectList.project.status.${project.status}`)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/user-project-detail/${project.projectId}`);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {t('userProjectList.buttons.viewDetails')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Navigation */}
      <div className="mt-8 flex justify-center">
        <Button
          onClick={() => navigate('/user-dashboard')}
          variant="outline"
        >
          {t('userProjectList.buttons.backToDashboard')}
        </Button>
      </div>
    </div>
  );
}

export default UserProjectListPage;
